/**
 * Maps a thrown chat error to a user-facing message in Remy's voice.
 *
 * Every failure path in the chat loop previously died in a console.error, which
 * is invisible in an installed PWA — the user just saw their message sit there
 * with no reply. These strings are what gets rendered in the transcript instead.
 */

/**
 * Thrown when the proxy returns HTTP 200 but no text and no tool calls — a
 * safety block or a MAX_TOKENS finish. Routed through the normal catch path so
 * the empty model turn is rolled back out of history rather than persisted.
 */
export const EMPTY_RESPONSE = 'EMPTY_RESPONSE';

export function remyErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const name = error instanceof Error ? error.name : '';

  // Thrown by authFetch on a 429 from the rate limiter or monthly usage cap.
  if (name === 'RateLimitError') {
    return "Ah — you've been keeping me busy. Give me a minute to catch my breath, then ask again.";
  }

  // authFetch throws this when there's no Firebase ID token to attach.
  if (message === 'Not authenticated') {
    return "Your session has slipped away. Sign out and back in, and I'll be right here.";
  }

  if (name === 'AbortError') {
    return 'Hm — that one got away from me before I could answer. Ask again?';
  }

  if (message === EMPTY_RESPONSE) {
    return 'I lost my train of thought there — nothing came back. Try asking me again?';
  }

  // fetch() rejects with a TypeError when the network is unreachable.
  if (name === 'TypeError') {
    return "I can't reach the cellar from here — the connection dropped. Check your signal and try again.";
  }

  const status = message.match(/Gemini proxy error: (\d+)/)?.[1];
  if (status === '401') {
    return "Your session has slipped away. Sign out and back in, and I'll be right here.";
  }
  if (status === '429') {
    return "Ah — you've been keeping me busy. Give me a minute to catch my breath, then ask again.";
  }
  // The proxy rejects an over-long conversation with a 400. Without this it
  // fell through to the generic message, which blames the server and leaves
  // the user re-sending something that can never succeed.
  if (status === '400') {
    return "This conversation has run long enough that I've lost the start of it. Start a new chat and ask me again — I'll still know your cellar.";
  }
  if (status && Number(status) >= 500) {
    return "Something's corked on my end, not yours. Give me a moment and try that again.";
  }

  // Checked last: navigator.onLine is unreliable in a Capacitor WebView, so a
  // stale false must not mask a definite status code parsed above.
  if (!navigator.onLine) {
    return "I can't reach the cellar from here — the connection dropped. Check your signal and try again.";
  }

  return "Something's corked on my end, not yours. Give me a moment and try that again.";
}
